import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { API_URL } from "../lib/supabase";
import { categoryGradients } from "../lib/constants";
import { CategoryIcon } from "../components/shared/CategoryIcon";
import { ServiceIcon } from "../components/shared/ServiceIcon";
import { MessageSquare, ChevronRight, ArrowRight } from "lucide-react";

const CategoriesPage = () => {
  const [categories, setCategories] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [catRes, svcRes] = await Promise.all([
        fetch(`${API_URL}/categories`),
        fetch(`${API_URL}/services`)
      ]);
      const catData = await catRes.json();
      const svcData = await svcRes.json();
      setCategories(catData);
      setServices(svcData);
    } catch (error) {
      console.error('Error:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) return <div className="loading">Loading...</div>;

  // Group services by category — ALL categories treated equally
  const grouped = categories.map(cat => ({
    ...cat,
    services: services.filter(s => s.category?.slug === cat.slug || s.category_id === cat.id)
  }));

  return (
    <div className="page categories-page">
      <div className="page-header">
        <h1>Browse Service Categories</h1>
        <p>Explore services across {categories.length} categories — and growing</p>
      </div>
      
      <div className="categories-grouped" data-testid="all-categories">
        {grouped.map(cat => (
          <div key={cat.id} className="category-group" data-testid={`category-group-${cat.slug}`}>
            <div className="category-group-header">
              <div className="category-group-title">
                <div className={`category-icon small ${categoryGradients[cat.slug] || 'cleaning'}`}>
                  <CategoryIcon iconName={cat.icon_name} />
                </div>
                <h2>{cat.name}</h2>
              </div>
              <Link to={`/categories/${cat.slug}`} className="category-view-all" data-testid={`view-${cat.slug}`}>
                View all <ChevronRight size={16} />
              </Link>
            </div>

            {cat.services.length > 0 ? (
              <div className="category-services-grid">
                {cat.services.slice(0, 4).map(service => (
                  <Link to={`/services/${service.slug}`} key={service.id} className="category-service-card" data-testid={`service-${service.slug}`}>
                    <div className={`svc-icon-header ${categoryGradients[cat.slug] || 'cleaning'}`}>
                      <ServiceIcon slug={service.slug} size={20} />
                    </div>
                    <div className="category-service-info">
                      <h4>{service.name}</h4>
                      <span className="service-price">{service.price_display}</span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="category-empty-state" data-testid={`empty-${cat.slug}`}>
                <MessageSquare size={20} />
                <p>No listed services yet — request a quote for any {cat.name.toLowerCase()} job.</p>
              </div>
            )}

            <div className="category-group-footer">
              {cat.services.length > 0 && (
                <Link to={`/categories/${cat.slug}`} className="btn-secondary btn-sm" data-testid={`view-all-${cat.slug}`}>
                  View All {cat.name} Services <ChevronRight size={14} />
                </Link>
              )}
              <Link to={`/quote-request/other?category=${cat.slug}`} className="btn-outline btn-sm" data-testid={`quote-other-${cat.slug}`}>
                Request a {cat.name} Quote <ArrowRight size={14} />
              </Link>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};


export default CategoriesPage;
